import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSyncExternalStore } from 'react';
import { logger } from '@/lib/logger';
import type { DownloadRecord } from './types';

/**
 * Download registry: the list of DownloadRecords, persisted in AsyncStorage
 * and mirrored in memory so UI can subscribe synchronously.
 */
const STORAGE_KEY = 'osj.offline.downloads.v1';

type Listener = () => void;

let records: Record<number, DownloadRecord> = {};
let loaded = false;
let loadPromise: Promise<void> | null = null;
const listeners = new Set<Listener>();
// Stable snapshot for useSyncExternalStore (must return the same reference
// until something changes).
let listSnapshot: DownloadRecord[] = [];

function refreshSnapshot(): void {
  listSnapshot = Object.values(records).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function emit(): void {
  refreshSnapshot();
  listeners.forEach((l) => {
    try {
      l();
    } catch (err) {
      logger.error('[offline] registry listener failed', err);
    }
  });
}

async function persist(): Promise<void> {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(records));
  } catch (err) {
    logger.error('[offline] registry persist failed', err);
  }
}

export function loadRegistry(): Promise<void> {
  if (loaded) return Promise.resolve();
  if (!loadPromise) {
    loadPromise = (async () => {
      try {
        const raw = await AsyncStorage.getItem(STORAGE_KEY);
        records = raw ? (JSON.parse(raw) as Record<number, DownloadRecord>) : {};
      } catch (err) {
        logger.error('[offline] registry load failed', err);
        records = {};
      }
      loaded = true;
      emit();
    })();
  }
  return loadPromise;
}

export function isRegistryLoaded(): boolean {
  return loaded;
}

export function getDownload(lessonVideoId: number): DownloadRecord | null {
  return records[lessonVideoId] ?? null;
}

export function getAllDownloads(): DownloadRecord[] {
  return listSnapshot;
}

export function upsertDownload(record: DownloadRecord): void {
  records = { ...records, [record.lessonVideoId]: record };
  emit();
  void persist();
}

export function patchDownload(lessonVideoId: number, patch: Partial<DownloadRecord>): DownloadRecord | null {
  const current = records[lessonVideoId];
  if (!current) return null;
  const next = { ...current, ...patch };
  records = { ...records, [lessonVideoId]: next };
  emit();
  void persist();
  return next;
}

export function removeDownload(lessonVideoId: number): void {
  if (!records[lessonVideoId]) return;
  const { [lessonVideoId]: _removed, ...rest } = records;
  records = rest;
  emit();
  void persist();
}

export function subscribeDownloads(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** All downloads, newest first. Re-renders on any registry change. */
export function useDownloads(): DownloadRecord[] {
  return useSyncExternalStore(subscribeDownloads, getAllDownloads, getAllDownloads);
}

const EMPTY: null = null;
/** One video's download record (or null). */
export function useDownload(lessonVideoId: number | null | undefined): DownloadRecord | null {
  return useSyncExternalStore(
    subscribeDownloads,
    () => (lessonVideoId == null ? EMPTY : records[lessonVideoId] ?? EMPTY),
    () => EMPTY,
  );
}

/** Test-only: reset in-memory state. */
export function __resetRegistryForTests(): void {
  records = {};
  loaded = false;
  loadPromise = null;
  listeners.clear();
  refreshSnapshot();
}
